import type { BlocksUser } from "@/src/models/user.model";
import { appRoles } from "@/src/constants/roles-permissions.constants";

const ROLE_PRIORITY = ["super-admin", "admin"] as const;

/**
 * `clouduser` is stripped first: it is a Blocks platform-access role, not an app
 * role, and it can arrive BEFORE the real one (IAM returns
 * `["admin","clouduser"]` in no guaranteed order). Without this the `roles[0]`
 * fallback could label an admin "clouduser" everywhere it is displayed.
 */
/**
 * Returns `""` when there is no app role — NOT a placeholder.
 *
 * This used to return the literal `"user"`, which is not a role this project has:
 * IAM knows `clouduser`, `member`, `admin` and `super-admin`/`super_admin`. So a
 * user whose only role was the platform `clouduser` (stripped by `appRoles`) was
 * labelled "user" everywhere a role is displayed — a name that exists nowhere in
 * the system, shown in place of their real one. Callers must decide what to
 * render for the empty case; an empty string is falsy so `??`/`||` chains fall
 * through to a better source instead of latching onto the placeholder.
 */
export const getPrimaryRole = (roles?: readonly string[] | null): string => {
  const candidates = appRoles(roles);
  if (!candidates.length) return "";
  // Match `super-admin` and `super_admin` alike — the two tenants disagree on
  // which separator the slug uses.
  const normalized = candidates.map((r) => r.toLowerCase().replace(/_/g, "-"));
  for (const priorityRole of ROLE_PRIORITY) {
    const hit = normalized.indexOf(priorityRole);
    if (hit !== -1) return candidates[hit];
  }
  return candidates[0];
};

/**
 * A role slug rendered for humans: `super_admin` / `super-admin` → `Super Admin`.
 *
 * Slugs are not display strings. The prod tenant's slug is `super_admin` and
 * dev's is `super-admin`, so anything that split on `-` alone printed
 * "Super_admin" in prod. Splits on every separator and title-cases each word.
 */
export const formatRoleLabel = (role?: string | null): string =>
  (role ?? "")
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

export const getUserOrgRoles = (
  blocksUser: BlocksUser,
  orgId?: string,
): string[] => {
  const membership = blocksUser?.memberships?.find(
    (m) => m.organizationId === orgId,
  );
  // De-dupe defensively, case-insensitively, while preserving original casing.
  const seen = new Set<string>();
  // App roles only — `clouduser` must not surface in role pickers or badges.
  return appRoles(membership?.roles).filter((role) => {
    const key = role.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};
